import { ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_WIDTH,
  cartesianFrame,
} from "@/lib/tool-kit/charts/layout";
import { linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { maxTextWidth } from "@/lib/tool-kit/charts/text";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";
import type { Paper } from "@/lib/tool-kit/reference-tools/h-index";

/*
 * Papers ranked by citations, with the line rank = citations. The h-index is where the
 * bars drop below the line: the last rank whose bar still reaches it. h-core bars are
 * blue, the rest grey, and the crossing is marked. Colours are hex so an exported SVG
 * or PNG keeps them.
 */

/** Beyond this many papers the bars are too thin to read; the chart shows the top. */
export const MAX_BARS = 100;

const CORE = CHART_COLORS[0];
const LINE = CHART_COLORS[3];
const REST = "#bdbdbd";

export function HIndexChart({
  id,
  ranked,
  h,
  label,
  description,
  rankTitle,
  citationsTitle,
  lineLabel,
  hLabel,
  fmt,
}: {
  id: string;
  ranked: readonly Paper[];
  h: number;
  label: string;
  description: string;
  rankTitle: string;
  citationsTitle: string;
  /** "rank = citations" */
  lineLabel: string;
  /** "h = 8" */
  hLabel: string;
  fmt: NumberFormat;
}) {
  const shown = ranked.slice(0, MAX_BARS);
  const n = shown.length;
  const top = Math.max(1, ...shown.map((paper) => paper.citations));
  const yTicks = niceTicks(0, top, { includeZero: true, integer: true });
  const tickLabels = yTicks.ticks.map((tick) => fmt(tick));
  const frame = cartesianFrame({
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
    xTitle: rankTitle,
    yTitle: citationsTitle,
    leftLabelWidth: maxTextWidth(tickLabels, {
      fontSize: CHART_FONT_SIZE.tick,
    }),
    bottomLabelHeight: CHART_FONT_SIZE.tick + 4,
  });
  const { plot } = frame;
  const x = linearScale([0, n], [plot.x, plot.x + plot.width]);
  const y = linearScale([0, yTicks.max], [plot.y + plot.height, plot.y]);
  const step = plot.width / Math.max(1, n);
  const gap = step > 6 ? step * 0.2 : 0;
  const xTicks = niceTicks(1, Math.max(1, n), {
    integer: true,
    exact: true,
  }).ticks.filter((tick) => tick >= 1 && tick <= n);
  const lineEnd = Math.min(n, yTicks.max);
  // Above the end of the line when there is room, so the label does not sit on it.
  const above = y(lineEnd) - 8;
  const labelY =
    above - CHART_FONT_SIZE.value > plot.y
      ? above
      : y(lineEnd) + CHART_FONT_SIZE.value + 6;
  return (
    <ChartSvg
      description={description}
      height={CHART_HEIGHT}
      id={id}
      label={label}
      width={CHART_WIDTH}
    >
      <ChartTitles frame={frame} />
      <ValueAxis
        format={(value) => fmt(value)}
        orient="left"
        plot={plot}
        scale={y}
        ticks={yTicks.ticks}
      />
      <ValueAxis
        format={(value) => fmt(value)}
        grid={false}
        orient="bottom"
        plot={plot}
        scale={(rank) => x(rank - 0.5)}
        ticks={xTicks}
      />
      {shown.map((paper, index) => (
        <rect
          fill={index < h ? CORE : REST}
          height={px(Math.max(0, y(0) - y(paper.citations)))}
          // Bars are ranks; position is their identity.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional bars
          key={index}
          width={px(Math.max(0.5, step - gap))}
          x={px(x(index) + gap / 2)}
          y={px(y(paper.citations))}
        />
      ))}
      <line
        stroke={LINE}
        strokeDasharray="6 4"
        strokeWidth={2}
        x1={px(x(0))}
        x2={px(x(lineEnd))}
        y1={px(y(0))}
        y2={px(y(lineEnd))}
      />
      <text
        fill={LINE}
        fontSize={CHART_FONT_SIZE.value}
        textAnchor="end"
        x={px(x(lineEnd) - 4)}
        y={px(labelY)}
      >
        {lineLabel}
      </text>
      {h > 0 ? (
        <g>
          <circle cx={px(x(h))} cy={px(y(h))} fill={CHART_INK.text} r={4} />
          <text
            fill={CHART_INK.text}
            fontSize={CHART_FONT_SIZE.tick}
            fontWeight="bold"
            x={px(x(h) + 8)}
            y={px(y(h) - 8)}
          >
            {hLabel}
          </text>
        </g>
      ) : null}
    </ChartSvg>
  );
}
